import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import Skeleton from "@material-ui/lab/Skeleton";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  hero: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 16,
    padding: theme.spacing(2.5),
    border: "1px solid rgba(2, 132, 199, 0.22)",
    background:
      "linear-gradient(135deg, rgba(14,165,233,0.14) 0%, rgba(2,132,199,0.10) 35%, rgba(16,185,129,0.06) 100%)",
    marginBottom: theme.spacing(2),
  },
  heroGlow: {
    position: "absolute",
    inset: -90,
    background:
      "radial-gradient(circle at 20% 10%, rgba(14,165,233,0.35), transparent 42%), radial-gradient(circle at 85% 35%, rgba(16,185,129,0.20), transparent 48%)",
    filter: "blur(22px)",
    pointerEvents: "none",
  },
  heroTitle: {
    fontWeight: 900,
    letterSpacing: -0.6,
    lineHeight: 1.1,
    color: "#052e4a",
  },
  heroSubtitle: {
    marginTop: theme.spacing(0.75),
    maxWidth: 760,
    color: "rgba(5, 46, 74, 0.78)",
  },
  heroStats: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1.5),
  },
  statPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(2, 132, 199, 0.18)",
    background: "rgba(255,255,255,0.66)",
    backdropFilter: "blur(6px)",
  },
  statValue: {
    fontWeight: 900,
    color: "#0b3a5a",
  },
  noManageHint: {
    marginTop: theme.spacing(1),
    color: "#64748b",
  },

  stickyBar: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    padding: theme.spacing(1.5),
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    background: theme.palette.background.paper || "#fff",
    marginBottom: theme.spacing(2),
  },
  toolbarRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    gap: theme.spacing(1),
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  divider: { margin: theme.spacing(2, 0) },
  chipsRow: {
    display: "flex",
    gap: theme.spacing(0.75),
    alignItems: "center",
    overflowX: "auto",
    padding: "10px 2px 2px",
    WebkitOverflowScrolling: "touch",
  },
  chipSmall: { height: 28, fontWeight: 800 },
  chipActive: {
    background: "rgba(14,165,233,0.12)",
    borderColor: "rgba(14,165,233,0.35)",
    color: "#0b3a5a",
  },

  card: {
    height: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 16px 36px rgba(15, 23, 42, 0.08)",
      borderColor: "rgba(2, 132, 199, 0.35)",
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1.25),
  },
  badge: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    letterSpacing: 0.6,
    color: "#0b3a5a",
    background:
      "linear-gradient(135deg, rgba(14,165,233,0.25) 0%, rgba(2,132,199,0.12) 55%, rgba(16,185,129,0.10) 100%)",
    border: "1px solid rgba(14,165,233,0.18)",
    flex: "0 0 auto",
  },
  titleWrap: { minWidth: 0 },
  addonTitle: {
    fontWeight: 900,
    letterSpacing: -0.2,
    lineHeight: 1.15,
    marginTop: 2,
  },
  muted: { color: "#64748b" },
  mono: { fontFamily: "monospace" },
  description: {
    marginTop: theme.spacing(0.75),
    color: "#475569",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  metaRow: {
    display: "flex",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(1.25),
  },
  priceRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.25),
  },
  price: {
    fontWeight: 900,
    letterSpacing: -0.2,
    color: "#0f172a",
  },
  statusChipOn: {
    background: "rgba(16, 185, 129, 0.12)",
    color: "#065f46",
    borderColor: "rgba(16, 185, 129, 0.35)",
    fontWeight: 800,
  },
  statusChipOff: {
    background: "rgba(245, 158, 11, 0.12)",
    color: "#92400e",
    borderColor: "rgba(245, 158, 11, 0.35)",
    fontWeight: 800,
  },
  actionsRow: {
    marginTop: theme.spacing(1.5),
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
  },

  emptyWrap: {
    padding: theme.spacing(3),
    borderRadius: 16,
    border: "1px dashed #cbd5e1",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    textAlign: "center",
  },
}));

export default function Marketplace() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [activeOnly, setActiveOnly] = useState(false);
  const [sort, setSort] = useState("recommended");
  const [modules, setModules] = useState([]);

  const canManage = useMemo(() => {
    const profile = String(user?.profile || "").toLowerCase();
    return Boolean(
      user?.super ||
        user?.is_superuser ||
        user?.isSuperuser ||
        profile === "admin" ||
        profile === "supervisor"
    );
  }, [user]);

  const addons = useMemo(() => {
    return (modules || []).filter((m) => !!m?.isAddon);
  }, [modules]);

  const categories = useMemo(() => {
    const set = new Set();
    (addons || []).forEach((m) => {
      if (m?.category) set.add(String(m.category));
    });
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [addons]);

  const filteredAddons = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    return (addons || [])
      .filter((m) =>
        category === "all" ? true : String(m?.category || "") === category
      )
      .filter((m) => (activeOnly ? Boolean(m?.effective) : true))
      .filter((m) => {
        if (!q) return true;
        const parts = [m?.name, m?.key, m?.category, m?.description]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return parts.includes(q);
      });
  }, [activeOnly, addons, category, search]);

  const sortedAddons = useMemo(() => {
    const list = [...(filteredAddons || [])];
    if (sort === "name_asc") {
      list.sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
          sensitivity: "base",
        })
      );
      return list;
    }
    if (sort === "price_asc") {
      list.sort((a, b) => Number(a?.amount || 0) - Number(b?.amount || 0));
      return list;
    }
    if (sort === "price_desc") {
      list.sort((a, b) => Number(b?.amount || 0) - Number(a?.amount || 0));
      return list;
    }

    // recommended
    list.sort((a, b) => {
      const ae = Boolean(a?.effective);
      const be = Boolean(b?.effective);
      if (ae !== be) return ae ? -1 : 1;

      const ap = Boolean(a?.planIncluded);
      const bp = Boolean(b?.planIncluded);
      if (ap !== bp) return ap ? -1 : 1;

      const aa = Number(a?.amount || 0);
      const ba = Number(b?.amount || 0);
      if (aa !== ba) return aa - ba;

      return String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
      });
    });

    return list;
  }, [filteredAddons, sort]);

  const load = async () => {
    if (!user?.companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${user.companyId}/modules`);
      setModules(Array.isArray(data?.modules) ? data.modules : []);
    } catch (err) {
      toastError(err);
      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  const updateAddon = async (module, enabled) => {
    if (!user?.companyId || !module?.id) return;
    setUpdatingId(module.id);
    try {
      await api.put(`/companies/${user.companyId}/modules`, {
        modules: [
          {
            moduleId: module.id,
            enabled,
            source: "addon",
            addedBy: user?.id,
          },
        ],
      });
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setUpdatingId(null);
    }
  };

  const stats = useMemo(() => {
    const total = addons.length;
    const active = addons.filter((m) => Boolean(m?.effective)).length;
    return { total, active };
  }, [addons]);

  const formatAmount = (value) => {
    const n = Number(value || 0);
    if (!n) return i18n.t("marketplace.card.free");
    try {
      // No currency field in API; keep it numeric but locale-aware.
      return new Intl.NumberFormat(i18n.language || undefined, {
        maximumFractionDigits: 2,
      }).format(n);
    } catch (_) {
      return String(n);
    }
  };

  const abbr = (m) => {
    const raw = String(m?.key || m?.name || "").trim();
    if (!raw) return "AD";
    const parts = raw
      .replace(/[_-]+/g, " ")
      .split(/\s+/)
      .filter(Boolean);
    const a = (parts[0] || raw)[0] || "A";
    const b = (parts[1] || parts[0] || raw)[0] || "D";
    return `${a}${b}`.toUpperCase();
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("marketplace.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            {i18n.t("marketplace.actions.refresh")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.hero}>
          <div className={classes.heroGlow} />
          <div style={{ position: "relative" }}>
            <Typography variant="h4" className={classes.heroTitle}>
              {i18n.t("marketplace.hero.title")}
            </Typography>
            <Typography variant="body1" className={classes.heroSubtitle}>
              {i18n.t("marketplace.hero.subtitle")}
            </Typography>

            <div className={classes.heroStats}>
              <div className={classes.statPill}>
                <span className={classes.muted}>
                  {i18n.t("marketplace.stats.active")}
                </span>
                <span className={classes.statValue}>{stats.active}</span>
              </div>
              <div className={classes.statPill}>
                <span className={classes.muted}>
                  {i18n.t("marketplace.stats.available")}
                </span>
                <span className={classes.statValue}>{stats.total}</span>
              </div>
              <div className={classes.statPill}>
                <span className={classes.muted}>
                  {i18n.t("marketplace.stats.showing")}
                </span>
                <span className={classes.statValue}>{sortedAddons.length}</span>
              </div>
            </div>

            {!canManage && (
              <Typography variant="body2" className={classes.noManageHint}>
                {i18n.t("marketplace.permissions.manageOnly")}
              </Typography>
            )}
          </div>
        </div>

        <div className={classes.stickyBar}>
          <div className={classes.toolbarRow}>
            <TextField
              size="small"
              variant="outlined"
              label={i18n.t("marketplace.search.label")}
              placeholder={i18n.t("marketplace.search.placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(Boolean(e.target.checked))}
                  color="primary"
                />
              }
              label={i18n.t("marketplace.filters.activeOnly")}
              style={{ marginRight: 0 }}
            />

            <FormControl variant="outlined" size="small">
              <Select
                value={sort}
                onChange={(e) => setSort(String(e.target.value))}
              >
                <MenuItem value="recommended">
                  {i18n.t("marketplace.filters.sort.recommended")}
                </MenuItem>
                <MenuItem value="name_asc">
                  {i18n.t("marketplace.filters.sort.nameAsc")}
                </MenuItem>
                <MenuItem value="price_asc">
                  {i18n.t("marketplace.filters.sort.priceAsc")}
                </MenuItem>
                <MenuItem value="price_desc">
                  {i18n.t("marketplace.filters.sort.priceDesc")}
                </MenuItem>
              </Select>
            </FormControl>
          </div>

          <Divider className={classes.divider} />

          <div className={classes.chipsRow}>
            <Chip
              className={`${classes.chipSmall} ${
                category === "all" ? classes.chipActive : ""
              }`}
              label={i18n.t("marketplace.filters.all")}
              clickable
              onClick={() => setCategory("all")}
              variant="outlined"
            />

            {(categories || []).map((c) => (
              <Chip
                key={c}
                className={`${classes.chipSmall} ${
                  category === c ? classes.chipActive : ""
                }`}
                label={c}
                clickable
                onClick={() => setCategory(c)}
                variant="outlined"
              />
            ))}
          </div>
        </div>

        {loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card className={classes.card} elevation={0}>
                  <CardContent>
                    <div className={classes.cardHeader}>
                      <div style={{ display: "flex", gap: 12, minWidth: 0 }}>
                        <div className={classes.badge}>
                          <Skeleton variant="text" width={22} height={22} />
                        </div>
                        <div className={classes.titleWrap} style={{ flex: 1 }}>
                          <Skeleton variant="text" width="70%" height={28} />
                          <Skeleton variant="text" width="92%" height={18} />
                        </div>
                      </div>
                      <Skeleton
                        variant="rect"
                        width={86}
                        height={26}
                        style={{ borderRadius: 999 }}
                      />
                    </div>

                    <Skeleton
                      variant="text"
                      width="98%"
                      height={18}
                      style={{ marginTop: 10 }}
                    />
                    <Skeleton variant="text" width="85%" height={18} />

                    <div className={classes.metaRow}>
                      <Skeleton
                        variant="rect"
                        width={120}
                        height={26}
                        style={{ borderRadius: 999 }}
                      />
                      <Skeleton
                        variant="rect"
                        width={140}
                        height={26}
                        style={{ borderRadius: 999 }}
                      />
                    </div>

                    <div className={classes.actionsRow}>
                      <Skeleton
                        variant="rect"
                        width={110}
                        height={36}
                        style={{ borderRadius: 10 }}
                      />
                      <Skeleton
                        variant="rect"
                        width={110}
                        height={36}
                        style={{ borderRadius: 10 }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {sortedAddons.map((m) => {
              const isBusy = updatingId === m.id;
              const enabled = Boolean(m?.effective);
              const override = m?.companyOverride;
              const hasOverride = override !== null && override !== undefined;

              return (
                <Grid item xs={12} md={4} key={m.id}>
                  <Card className={classes.card} elevation={0}>
                    <CardContent>
                      <div className={classes.cardHeader}>
                        <div style={{ display: "flex", gap: 12, minWidth: 0 }}>
                          <div className={classes.badge}>{abbr(m)}</div>
                          <div className={classes.titleWrap}>
                            <Typography
                              variant="h6"
                              className={classes.addonTitle}
                              noWrap
                            >
                              {m.name}
                            </Typography>
                            <Typography variant="caption" className={classes.muted}>
                              {i18n.t("marketplace.card.key")}{" "}
                              <span className={classes.mono}>{String(m.key)}</span>
                            </Typography>
                          </div>
                        </div>

                        <Chip
                          variant="outlined"
                          label={
                            enabled
                              ? i18n.t("marketplace.card.active")
                              : i18n.t("marketplace.card.inactive")
                          }
                          className={
                            enabled ? classes.statusChipOn : classes.statusChipOff
                          }
                        />
                      </div>

                      <Typography variant="body2" className={classes.description}>
                        {m.description || i18n.t("marketplace.card.noDescription")}
                      </Typography>

                      <div className={classes.metaRow}>
                        <Chip
                          className={classes.chipSmall}
                          variant="outlined"
                          label={`${i18n.t("marketplace.card.category")}: ${String(
                            m.category || "addon"
                          )}`}
                        />
                        {Boolean(m.planIncluded) && (
                          <Chip
                            className={classes.chipSmall}
                            variant="outlined"
                            label={i18n.t("marketplace.card.includedInPlan")}
                          />
                        )}
                        {hasOverride && (
                          <Chip
                            className={classes.chipSmall}
                            variant="outlined"
                            label={i18n.t("marketplace.card.customized")}
                          />
                        )}
                      </div>

                      <div className={classes.priceRow}>
                        <div>
                          <Typography variant="caption" className={classes.muted}>
                            {i18n.t("marketplace.card.price")}
                          </Typography>
                          <Typography variant="h6" className={classes.price}>
                            {formatAmount(m.amount)}
                          </Typography>
                        </div>
                        {isBusy && <CircularProgress size={18} />}
                      </div>

                      <div className={classes.actionsRow}>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={!canManage || isBusy}
                          onClick={() => updateAddon(m, !enabled)}
                        >
                          {enabled
                            ? i18n.t("marketplace.actions.deactivate")
                            : i18n.t("marketplace.actions.activate")}
                        </Button>
                        {hasOverride && (
                          <Button
                            variant="outlined"
                            disabled={!canManage || isBusy}
                            onClick={() => updateAddon(m, null)}
                          >
                            {i18n.t("marketplace.actions.restore")}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}

            {sortedAddons.length === 0 && (
              <Grid item xs={12}>
                <div className={classes.emptyWrap}>
                  <Typography variant="h6" style={{ fontWeight: 900 }}>
                    {i18n.t("marketplace.empty.title")}
                  </Typography>
                  <Typography variant="body2" className={classes.noManageHint}>
                    {i18n.t("marketplace.empty.subtitle")}
                  </Typography>
                </div>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>
    </MainContainer>
  );
}

